import { useEffect, useMemo, useState } from 'react';
import { AutomationExecutionStatus } from '../../../generated/graphql';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from '../../components/Button';
import TableContainer from '../../components/TableContainer';
import { useAutomationExecutionsQuery } from '../../../generated/graphql';
import ExecutionsTable from './ExecutionsTable';
import { parseUtcIso } from '../../lib/time';
import { AutomationExecution, AutomationRulePartial } from './types';

type Props = {
  rules: AutomationRulePartial[];
};

const STATUS_MAP: Record<string, AutomationExecutionStatus> = {
  success: AutomationExecutionStatus.Success,
  failure: AutomationExecutionStatus.Failure,
  skipped: AutomationExecutionStatus.Skipped,
};

export default function ExecutionsTab({ rules }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedRuleId, setSelectedRuleId] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [didApplyRouteState, setDidApplyRouteState] = useState(false);
  const [timePreset, setTimePreset] = useState<
    'all' | 'last_hour' | 'last_day' | 'last_week' | 'custom'
  >('all');
  const [customFrom, setCustomFrom] = useState<string>('');
  const [customTo, setCustomTo] = useState<string>('');

  const { data, loading, error, refetch } = useAutomationExecutionsQuery({
    variables: {
      limit: 500,
      ...(selectedRuleId ? { automationRuleId: selectedRuleId } : {}),
      ...(selectedStatus && STATUS_MAP[selectedStatus]
        ? { status: STATUS_MAP[selectedStatus] }
        : {}),
    },
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (didApplyRouteState) return;
    const query = new URLSearchParams(location.search);
    const queryRuleId = query.get('ruleId') || undefined;
    const queryView = query.get('view');
    const state = location.state as {
      automationRuleId?: string;
      executionsView?: 'summary' | 'runs';
    } | null;

    const incomingRuleId = state?.automationRuleId || queryRuleId;
    const incomingView =
      state?.executionsView ||
      (queryView === 'summary' || queryView === 'runs' ? queryView : undefined);

    if (!incomingRuleId && !incomingView) {
      setDidApplyRouteState(true);
      return;
    }

    if (incomingRuleId) {
      setSelectedRuleId(incomingRuleId);
    }

    setDidApplyRouteState(true);
    navigate('/automations/history', { replace: true, state: null });
  }, [didApplyRouteState, location.search, location.state, navigate]);

  const executions = useMemo(() => {
    const rows = (data?.automationExecutions ?? []) as AutomationExecution[];
    const now = Date.now();

    return rows.filter((row) => {
      const startedAtMs = row.startedAt
        ? parseUtcIso(row.startedAt).toMillis()
        : NaN;
      const hasValidStartedAt = Number.isFinite(startedAtMs) && startedAtMs > 0;

      if (timePreset === 'all') {
        return true;
      }
      if (!hasValidStartedAt) return false;

      if (timePreset === 'last_hour') {
        return startedAtMs >= now - 60 * 60 * 1000;
      }
      if (timePreset === 'last_day') {
        return startedAtMs >= now - 24 * 60 * 60 * 1000;
      }
      if (timePreset === 'last_week') {
        return startedAtMs >= now - 7 * 24 * 60 * 60 * 1000;
      }

      const fromMs = customFrom ? new Date(customFrom).getTime() : null;
      const toMs = customTo ? new Date(customTo).getTime() : null;
      if (fromMs && startedAtMs < fromMs) return false;
      if (toMs && startedAtMs > toMs) return false;
      return true;
    });
  }, [customFrom, customTo, data?.automationExecutions, timePreset]);

  return (
    <div className="h-full">
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="text-xs text-neutral-130 mb-1 block font-semibold">
            Automation
          </label>
          <select
            value={selectedRuleId}
            onChange={(event) => setSelectedRuleId(event.target.value)}
            className="text-sm min-w-[220px] rounded-md border border-neutral-75 bg-neutral-50 py-2 pl-3 pr-10 focus:border-secondary-100 focus:outline-none focus:ring-2 focus:ring-secondary-50"
          >
            <option value="">All</option>
            {rules.map((rule) => (
              <option key={rule.id} value={rule.automationRuleId || rule.id}>
                {rule.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-neutral-130 mb-1 block font-semibold">
            Status
          </label>
          <select
            value={selectedStatus}
            onChange={(event) => setSelectedStatus(event.target.value)}
            className="text-sm min-w-[220px] rounded-md border border-neutral-75 bg-neutral-50 py-2 pl-3 pr-10 focus:border-secondary-100 focus:outline-none focus:ring-2 focus:ring-secondary-50"
          >
            <option value="">All</option>
            <option value="success">Success</option>
            <option value="failure">Failure</option>
            <option value="skipped">Skipped</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-neutral-130 mb-1 block font-semibold">
            Time
          </label>
          <select
            value={timePreset}
            onChange={(event) =>
              setTimePreset(
                event.target.value as
                  | 'all'
                  | 'last_hour'
                  | 'last_day'
                  | 'last_week'
                  | 'custom',
              )
            }
            className="text-sm min-w-[180px] rounded-md border border-neutral-75 bg-neutral-50 py-2 pl-3 pr-10 focus:border-secondary-100 focus:outline-none focus:ring-2 focus:ring-secondary-50"
          >
            <option value="all">All</option>
            <option value="last_hour">Last hour</option>
            <option value="last_day">Last day</option>
            <option value="last_week">Last week</option>
            <option value="custom">Custom range</option>
          </select>
        </div>
        {timePreset === 'custom' && (
          <>
            <div>
              <label className="text-xs text-neutral-130 mb-1 block font-semibold">
                From
              </label>
              <input
                type="datetime-local"
                value={customFrom}
                onChange={(event) => setCustomFrom(event.target.value)}
                className="text-sm rounded-md border border-neutral-75 bg-neutral-50 px-3 py-2 focus:border-secondary-100 focus:outline-none focus:ring-2 focus:ring-secondary-50"
              />
            </div>
            <div>
              <label className="text-xs text-neutral-130 mb-1 block font-semibold">
                To
              </label>
              <input
                type="datetime-local"
                value={customTo}
                onChange={(event) => setCustomTo(event.target.value)}
                className="text-sm rounded-md border border-neutral-75 bg-neutral-50 px-3 py-2 focus:border-secondary-100 focus:outline-none focus:ring-2 focus:ring-secondary-50"
              />
            </div>
          </>
        )}
        <Button
          title="Refresh"
          theme="secondary"
          size="small"
          onClick={() => refetch()}
        />
      </div>

      {error ? (
        <div className="text-sm rounded-md border border-red-100 bg-red-25 p-4 text-red-150">
          <p className="font-semibold">Failed to load history</p>
          <p className="mt-1">{error.message}</p>
          <Button
            title="Retry"
            theme="secondary"
            size="small"
            className="mt-3"
            onClick={() => refetch()}
          />
        </div>
      ) : (
        <TableContainer containerClassName="h-[calc(100vh-280px)] pb-0">
          <div className="w-full">
            {loading && !data ? (
              <div className="text-sm text-neutral-120 rounded-md border border-neutral-100 bg-white p-6">
                Loading...
              </div>
            ) : executions.length === 0 ? (
              <div className="text-sm text-neutral-120 rounded-md border border-neutral-100 bg-white p-6">
                No runs for the selected filters.
              </div>
            ) : (
              <ExecutionsTable executions={executions} rules={rules} />
            )}
          </div>
        </TableContainer>
      )}
    </div>
  );
}
